import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Copy, Loader2, PackageCheck, Truck, X } from 'lucide-react';
import { MaskedLines, Reveal, EASE } from './hammer/reveal';
import { useToast } from '../hooks/use-toast';

const CARRIERS = [
  { value: 'ltl_freight', label: 'LTL Freight (best-rate, we shop carriers)' },
];
const CARRIER_LABELS = Object.fromEntries(CARRIERS.map((c) => [c.value, c.label]));
const money = (n) => `$${Number(n).toLocaleString('en-US')}`;

export default function ShippingEstimator() {
  const { toast } = useToast();
  const [zip, setZip] = useState('');
  const [carrier, setCarrier] = useState('ltl_freight');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [barDismissed, setBarDismissed] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const sectionRef = useRef(null);
  const resultRef = useRef(null);

  const estimate = async (z = zip, c = carrier) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipping/estimate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ zip: z, carrier: c }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not estimate that ZIP');
      setResult(data);
      setBarDismissed(false);
    } catch (e) {
      setError(e.message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Share-link rehydration: ?zip=80202&carrier=ups_ground auto-runs + scrolls
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const z = params.get('zip');
    const c = params.get('carrier');
    if (z && /^\d{5}$/.test(z) && CARRIERS.some((k) => k.value === c)) {
      setZip(z);
      setCarrier(c);
      estimate(z, c);
      setTimeout(
        () => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        300
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sticky bar: only while a result exists, not dismissed, and both the
  // result panel and the quote form are off-screen.
  useEffect(() => {
    if (!result) {
      setShowBar(false);
      return;
    }
    const quoteEl = document.getElementById('hammer-quote');
    let resultVisible = false;
    let quoteVisible = false;
    const update = () => setShowBar(!barDismissed && !resultVisible && !quoteVisible);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.target === resultRef.current) resultVisible = en.isIntersecting;
          if (en.target === quoteEl) quoteVisible = en.isIntersecting;
        });
        update();
      },
      { threshold: 0.1 }
    );
    if (resultRef.current) io.observe(resultRef.current);
    if (quoteEl) io.observe(quoteEl);
    update();
    return () => io.disconnect();
  }, [result, barDismissed]);

  const quoteMessage = result
    ? `Hi, I'd like a quote on the Absolute Hammer shipped to ${result.dest_city}, ${result.dest_state} (ZIP ${result.zip}) via ${CARRIER_LABELS[result.carrier]}. Your online estimator showed ${money(result.low)} – ${money(result.high)} (${result.distance_miles} mi from Fort Myers). Please confirm final pricing and lead time.`
    : '';

  const addToQuote = () => {
    window.dispatchEvent(
      new CustomEvent('prefill-quote-message', { detail: { message: quoteMessage } })
    );
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?zip=${result.zip}&carrier=${result.carrier}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Share link copied to clipboard.' });
    } catch {
      toast({ title: 'Copy this link', description: url });
    }
  };

  return (
    <section
      id="shipping"
      ref={sectionRef}
      data-testid="shipping-estimator-section"
      className="relative bg-black border-t border-white/5 py-16 sm:py-20 lg:py-28 overflow-hidden"
    >
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 mb-3 sm:mb-4">
            <span className="h-px w-8 sm:w-10 bg-cyan-400" />
            <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
              Nationwide Shipping
            </span>
          </div>
        </Reveal>
        <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.02] tracking-tight">
          <MaskedLines
            delay={0.15}
            lines={[
              'Freight,',
              <span className="text-cyan-300">estimated in seconds.</span>,
            ]}
          />
        </h2>
        <Reveal delay={0.3}>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base md:text-lg leading-relaxed">
            The hammer ships crated at 850 lb from Fort Myers, FL — LTL freight
            is the only way to move that much weight. Drop your ZIP and get a
            real-world freight range — final pricing is confirmed with your
            quote.
          </p>
        </Reveal>

        <Reveal delay={0.4}>
          <form
            className="mt-8 panel rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row gap-3 sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              estimate();
            }}
          >
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
                Ship-to ZIP <span className="text-cyan-300">*</span>
              </label>
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                inputMode="numeric"
                pattern="\d{5}"
                maxLength={5}
                required
                placeholder="80202"
                className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                data-testid="shipping-zip-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || zip.length !== 5}
              className="btn-teal px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="shipping-estimate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
              Estimate
            </button>
          </form>
          <p className="mt-3 text-zinc-500 text-[12px] leading-relaxed" data-testid="shipping-freight-note">
            Ships LTL freight (850 lb crated) — we shop FedEx Freight, TForce, R+L
            and more to get you the best rate on your lane.
          </p>
        </Reveal>

        {error && (
          <div
            className="mt-4 text-red-400 text-sm panel rounded-lg px-4 py-3"
            data-testid="shipping-error"
          >
            {error}
          </div>
        )}

        <AnimatePresence>
          {result && (
            <motion.div
              key={`${result.zip}-${result.carrier}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="mt-6 panel rounded-xl p-6 sm:p-8"
              data-testid="shipping-result"
            >
              <div ref={resultRef}>
                <div className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.24em]">
                  {result.carrier_label}
                </div>
                <div className="mt-3 font-display text-white text-4xl sm:text-5xl font-bold tracking-tight">
                  {money(result.low)} <span className="text-zinc-500 text-2xl sm:text-3xl">–</span>{' '}
                  <span className="text-cyan-300">{money(result.high)}</span>
                </div>
                <div className="mt-2 text-zinc-400 text-sm">
                  to {result.dest_city}, {result.dest_state} · {result.distance_miles} mi from Fort
                  Myers · Zone {result.zone}
                </div>
                <p className="mt-4 text-zinc-500 text-[12px] leading-relaxed">
                  Estimate only — final freight is confirmed with your written quote. Residential
                  delivery, liftgate, or jobsite timing may adjust the final rate.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={addToQuote}
                    className="btn-teal px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
                    data-testid="shipping-add-to-quote-btn"
                  >
                    <PackageCheck className="w-4 h-4" /> Add to my quote
                  </button>
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="btn-ghost px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
                    data-testid="shipping-share-btn"
                  >
                    <Copy className="w-4 h-4" /> Copy share link
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Sticky bottom bar — appears once a result exists and both the result
          panel and the quote form are scrolled out of view */}
      <AnimatePresence>
        {showBar && result && (
          <motion.div
            key="shipping-sticky"
            initial={{ y: 96, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 96, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed bottom-3 left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-40"
            data-testid="shipping-sticky-bar"
          >
            <div className="mx-auto max-w-xl panel rounded-xl border border-cyan-400/30 bg-black/90 backdrop-blur-md px-4 py-3 flex items-center gap-3 shadow-[0_8px_40px_rgba(34,211,238,0.15)]">
              <Truck className="w-4 h-4 text-cyan-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold truncate">
                  {money(result.low)} – {money(result.high)}{' '}
                  <span className="text-zinc-500 font-normal">
                    to {result.dest_city}, {result.dest_state}
                  </span>
                </div>
                <div className="text-zinc-500 text-[11px] truncate">{result.carrier_label}</div>
              </div>
              <button
                type="button"
                onClick={addToQuote}
                className="btn-teal px-4 py-2 rounded-md text-xs uppercase tracking-[0.14em] whitespace-nowrap"
                data-testid="shipping-sticky-cta"
              >
                Add to quote
              </button>
              <button
                type="button"
                onClick={() => setBarDismissed(true)}
                className="text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
                aria-label="Dismiss shipping estimate bar"
                data-testid="shipping-sticky-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
